import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { updateFrame, regenerateFrame, deleteFrame, clearStoryboard, insertShot, getFrameImageUrl, upgradeFrameStyle, migrateProjectConcepts } from "@/services/shotService";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { IconStoryboard, IconSparkle, IconSketchBadge, IconConceptBadge, IconPrevisBadge } from "@/components/Icons";
import { Film, Edit3, Loader2, AlertCircle, Plus, Play, Grid, List, X, ChevronLeft, ChevronRight, Maximize2, Trash2, GitBranch, Monitor, Users } from "lucide-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import AddShotDialog from "@/components/AddShotDialog";
import FrameFilmstrip from "@/components/FrameFilmstrip";
import ShotVersionPanel from "@/components/ShotVersionPanel";
import AutoStoryboardProgress from "@/components/AutoStoryboardProgress";
import ShotRedesignPanel from "@/components/storyboard/ShotRedesignPanel";
import FollowMode, { CollaboratorPip } from "@/components/storyboard/FollowMode";
import CinemaMode from "@/components/storyboard/CinemaMode";
import ConceptBoard from "@/components/storyboard/ConceptBoard";
import { useCollaboration } from "@/contexts/CollaborationContext";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_ICON_COMPONENTS = { sketch: IconSketchBadge, concept: IconConceptBadge, previs: IconPrevisBadge };
const STAGE_LABELS = { sketch: "Sketch", concept: "Concept", previs: "Previs" };

// Convert aspect ratio string to CSS value
function getAspectCss(ar) {
  if (!ar || ar === "16:9") return "16/9";
  const parts = ar.split(":");
  if (parts.length === 2) return `${parts[0]}/${parts[1]}`;
  return "16/9";
}

// Premium Frame Card
function FrameCard({ frame, isSelected, onClick, onEdit, onDelete }) {
  const isCompleted = frame.status === "completed";
  const isGenerating = frame.status === "generating";
  const isFailed = frame.status === "failed";
  const aspectCss = getAspectCss(frame.aspect_ratio);

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`card card-interactive p-0 overflow-hidden relative group ${isSelected ? 'card-selected' : ''}`}
      data-testid={`frame-card-${frame.id}`}
    >
      {/* Collaborator pip — shows who else is viewing this frame */}
      <CollaboratorPip frameId={frame.id} />

      {/* Delete button - shows on hover */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete?.(frame); }}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-black/60 text-white/70 hover:text-[var(--color-error)] hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-all"
        title="Delete shot"
      >
        <Trash2 size={14} />
      </button>

      {/* Frame Image — respects aspect ratio */}
      <div
        className={`relative overflow-hidden bg-[var(--bg-muted)] ${isGenerating ? 'animate-pulse' : ''}`}
        style={{ aspectRatio: aspectCss }}
      >
        {isCompleted ? (
          <img
            src={getFrameImageUrl(frame.id)}
            alt={`Shot ${frame.frame_number}`}
            className="w-full h-full object-cover"
            loading="lazy"
            crossOrigin="use-credentials"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[var(--bg-overlay)] to-[var(--bg-muted)]">
            {isGenerating ? (
              <Loader2 className="w-8 h-8 text-[var(--color-gold-400)] animate-spin" />
            ) : isFailed ? (
              <AlertCircle className="w-8 h-8 text-[var(--color-error)]" />
            ) : (
              <Film className="w-8 h-8 text-[var(--text-disabled)]" />
            )}
          </div>
        )}

        {/* Status + Stage indicator */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          {isCompleted && <span className="chip chip-success text-[9px]">Ready</span>}
          {isGenerating && <span className="chip chip-warning text-[9px]">Generating</span>}
          {isFailed && <span className="chip chip-error text-[9px]">Failed</span>}
          {frame.stage && (() => {
            const StageIcon = STAGE_ICON_COMPONENTS[frame.stage];
            return (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-black/50 text-white/80 backdrop-blur-sm flex items-center gap-1">
                {StageIcon && <StageIcon size={10} />} {STAGE_LABELS[frame.stage] || frame.stage}
              </span>
            );
          })()}
        </div>

        {/* Edit button */}
        <motion.button
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="absolute top-3 right-3 btn-ghost btn-icon-xs bg-[var(--bg-app)]/60 backdrop-blur-sm opacity-0 group-hover:opacity-100"
          data-testid={`edit-frame-btn-${frame.id}`}
        >
          <Edit3 size={14} />
        </motion.button>

        {/* Bottom gradient info */}
        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-white">Shot {frame.frame_number}</p>
              {frame.shot_type && (
                <p className="text-[10px] text-[var(--color-gold-400)] capitalize mt-0.5">
                  {frame.shot_type.replace('-', ' ')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {frame.aspect_ratio && frame.aspect_ratio !== "16:9" && (
                <span className="text-[9px] text-white/50">{frame.aspect_ratio}</span>
              )}
              {frame.duration_estimate && (
                <span className="text-[10px] text-white/60">{frame.duration_estimate}s</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Frame Lightbox
function FrameLightbox({ frame, frames, onClose, onPrev, onNext, onRegenerate, onVersions, onNavigate, onUpdate }) {
  const currentIndex = frames.findIndex(f => f.id === frame?.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < frames.length - 1;

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      // Don't intercept if user is typing in a textarea/input
      if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT") return;
      if (e.key === "ArrowLeft" && hasPrev) onPrev();
      else if (e.key === "ArrowRight" && hasNext) onNext();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [hasPrev, hasNext, onPrev, onNext, onClose]);

  if (!frame) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[var(--bg-app)]/95 backdrop-blur-xl flex items-center justify-center p-8"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-10 btn-ghost btn-icon"
        data-testid="close-lightbox"
      >
        <X size={20} />
      </button>

      {/* Navigation */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-10 btn-secondary btn-icon"
        >
          <ChevronLeft size={24} />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-10 btn-secondary btn-icon"
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* Content */}
      <div
        className="w-full max-w-6xl max-h-[90vh] flex gap-6 mx-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image */}
        <div className="flex-1 flex items-center justify-center">
          {frame.status === "completed" ? (
            <TransformWrapper key={frame.id}>
              <TransformComponent>
                <img
                  src={getFrameImageUrl(frame.id)}
                  alt={`Shot ${frame.frame_number}`}
                  className="max-h-[80vh] rounded-2xl shadow-2xl"
                  crossOrigin="use-credentials"
                />
              </TransformComponent>
            </TransformWrapper>
          ) : (
            <div className="w-full aspect-video rounded-2xl bg-[var(--bg-overlay)] flex items-center justify-center">
              <Film size={64} className="text-[var(--text-disabled)]" />
            </div>
          )}
        </div>

        {/* Shot Redesign Panel */}
        <ShotRedesignPanel
          key={frame.id}
          frame={frame}
          onRegenerate={onRegenerate}
          onVersions={onVersions}
          onUpdate={onUpdate}
        />
      </div>

      {/* Thumbnail Strip */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
        <div className="flex gap-2 p-2 bg-[var(--bg-surface)] rounded-xl border border-[var(--border-subtle)]">
          {frames.slice(Math.max(0, currentIndex - 3), currentIndex + 4).map((f) => (
            <button
              key={f.id}
              onClick={(e) => { e.stopPropagation(); if (f.id !== frame.id) onNavigate(f); }}
              className={`w-16 aspect-video rounded-lg overflow-hidden transition-all ${
                f.id === frame.id ? 'ring-2 ring-[var(--color-gold-500)]' : 'opacity-50 hover:opacity-100'
              }`}
            >
              {f.status === "completed" ? (
                <img src={getFrameImageUrl(f.id)} alt="" className="w-full h-full object-cover" loading="lazy" crossOrigin="use-credentials" />
              ) : (
                <div className="w-full h-full bg-[var(--bg-muted)]" />
              )}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function StoryboardPage({ projectId, project, frames, onUpdate, autoGenActive, onAutoGenDismiss }) {
  const conceptLayerEnabled = project?.concept_layer_enabled || false;
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [insertAfter, setInsertAfter] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or timeline
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [deletingFrame, setDeletingFrame] = useState(null);
  const [versionFrame, setVersionFrame] = useState(null);
  const [conceptBoardAnchor, setConceptBoardAnchor] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const { onEvent, emitFrameUpdate, emitViewportState, followingUser, setFollowingUser } = useCollaboration();
  const { user } = useAuth();

  const handleEnableConceptLayer = async () => {
    setMigrating(true);
    try {
      const result = await migrateProjectConcepts(projectId);
      toast.success(`Concept layer enabled! ${result.concepts_created} concepts created.`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  // Broadcast viewport state when selected frame changes
  useEffect(() => {
    if (emitViewportState) {
      emitViewportState({ selected_frame_id: selectedFrame?.id || null, view_mode: viewMode });
    }
  }, [selectedFrame?.id, viewMode]);

  // Auto-unfollow on manual interaction
  const handleManualFrameSelect = (frame) => {
    if (followingUser) setFollowingUser?.(null);
    if (conceptLayerEnabled && frame.anchor_id) {
      // Find all frames in the same shot group (multi-frame) or just this one
      let groupFrames = [frame];
      if (frame.shot_group_id) {
        groupFrames = frames.filter(f => f.shot_group_id === frame.shot_group_id);
      }
      setConceptBoardAnchor({
        frames: groupFrames,
        scene_number: frame.scene_number,
        selected_text: frame.prompt,
      });
    } else {
      setSelectedFrame(frame);
    }
  };

  // Listen for real-time frame updates from other users
  useEffect(() => {
    if (!onEvent) return;
    const unsub = onEvent('frame_updated', (data) => {
      toast.info(`${data.user_name || 'A collaborator'} updated Shot ${data.frame_id?.slice(-6) || ''}`);
      onUpdate();
    });
    return unsub;
  }, [onEvent, onUpdate]);

  useEffect(() => {
    if (!onEvent) return;
    const unsub = onEvent('new_shot_version', (data) => {
      toast.info(`${data.user_name || 'A collaborator'} saved a new version`);
    });
    return unsub;
  }, [onEvent]);

  const completedCount = frames.filter(f => f.status === "completed").length;
  
  // Group frames by scene
  const framesByScene = frames.reduce((acc, frame) => {
    if (!acc[frame.scene_number]) acc[frame.scene_number] = [];
    acc[frame.scene_number].push(frame);
    return acc;
  }, {});

  const handleRegenerate = async (frameId, newPrompt, equipment = {}) => {
    try {
      await regenerateFrame(frameId, { prompt: newPrompt, ...equipment });
      toast.success("Frame queued for regeneration");
      onUpdate();
    } catch (error) {
      toast.error("Failed to regenerate");
    }
  };

  const handleDeleteFrame = async (frame) => {
    try {
      await deleteFrame(frame.id);
      toast.success("Shot deleted");
      setDeletingFrame(null);
      if (selectedFrame?.id === frame.id) {
        setSelectedFrame(null);
      }
      onUpdate();
    } catch (error) {
      toast.error("Failed to delete shot");
    }
  };

  const handleClearStoryboard = async () => {
    try {
      await clearStoryboard(projectId);
      toast.success("Storyboard cleared");
      setShowClearConfirm(false);
      setSelectedFrame(null);
      onUpdate();
    } catch (error) {
      toast.error("Failed to clear storyboard");
    }
  };

  const handleAddShot = async (newShotData) => {
    try {
      await insertShot({
        project_id: projectId,
        scene_number: insertAfter?.scene_number || 1,
        insert_after_frame: insertAfter?.frame_number || null,
        prompt: newShotData.prompt,
        shot_type: newShotData.shot_type,
        stage: newShotData.stage || "sketch",
        aspect_ratio: newShotData.aspect_ratio || "16:9",
      });
      toast.success("Shot added and generation started");
      setShowAddDialog(false);
      setInsertAfter(null);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add shot");
    }
  };

  const currentFrameIndex = selectedFrame ? frames.findIndex(f => f.id === selectedFrame.id) : -1;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="h-14 px-6 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <span className="text-sm text-[var(--text-muted)]">
            {completedCount} of {frames.length} frames ready
          </span>
          {frames.length > 0 && (
            <div className="flex items-center">
              <div className="w-24 h-1.5 bg-[var(--bg-overlay)] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[var(--color-gold-600)] to-[var(--color-gold-400)] rounded-full transition-all"
                  style={{ width: `${(completedCount / frames.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Follow Mode — Collaborator Avatars */}
          <FollowMode onScrollToFrame={(frameId) => {
            const f = frames.find(fr => fr.id === frameId);
            if (f) setSelectedFrame(f);
          }} />

          {/* View Mode Toggle */}
          <div className="flex bg-[var(--bg-overlay)] rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`btn-icon-xs ${viewMode === 'grid' ? 'bg-[var(--bg-muted)] text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}`}
              title="Grid view"
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`btn-icon-xs ${viewMode === 'timeline' ? 'bg-[var(--bg-muted)] text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}`}
              title="Timeline view"
            >
              <List size={16} />
            </button>
            <button
              onClick={() => setViewMode('cinema')}
              className={`btn-icon-xs ${viewMode === 'cinema' ? 'bg-[var(--bg-muted)] text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}`}
              title="Cinema mode"
            >
              <Monitor size={16} />
            </button>
          </div>
          
          {/* Clear Storyboard Button */}
          {frames.length > 0 && (
            <Button 
              onClick={() => setShowClearConfirm(true)} 
              className="btn-ghost text-[var(--color-error)] hover:bg-[var(--color-error)]/10"
              data-testid="clear-storyboard-btn"
            >
              <Trash2 size={16} />
              <span className="ml-2">Clear All</span>
            </Button>
          )}
          
          <Button onClick={() => { setInsertAfter(null); setShowAddDialog(true); }} className="btn-primary" data-testid="add-shot-btn">
            <Plus size={16} />
            <span className="ml-2">Add Shot</span>
          </Button>
        </div>
      </div>

      {/* Enable Concept Layer Banner */}
      {!conceptLayerEnabled && frames.length > 0 && (
        <div className="px-6 pt-3 pb-0 flex-shrink-0">
          <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-gradient-to-r from-[var(--color-gold-500)]/5 to-[var(--color-teal-500)]/5 border border-[var(--color-gold-500)]/15">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[var(--color-gold-500)]/10 flex items-center justify-center">
                <Users size={16} className="text-[var(--color-gold-400)]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Concept Collaboration</p>
                <p className="text-xs text-[var(--text-muted)]">Let team members propose competing visions for each shot, then pick the best one together.</p>
              </div>
            </div>
            <Button
              onClick={handleEnableConceptLayer}
              disabled={migrating}
              className="btn-primary flex-shrink-0"
            >
              {migrating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Users size={14} className="mr-2" />}
              {migrating ? 'Enabling...' : 'Enable'}
            </Button>
          </div>
        </div>
      )}

      {/* Inline Auto-Generation Progress */}
      {autoGenActive && projectId && (
        <div className="px-6 pt-4 pb-0 flex-shrink-0">
          <AutoStoryboardProgress
            projectId={projectId}
            inline={true}
            onComplete={() => {
              onAutoGenDismiss?.();
              onUpdate();
              toast.success("Full storyboard generation complete!");
            }}
            onDismiss={onAutoGenDismiss}
          />
        </div>
      )}

      {/* Content */}
      {viewMode === 'cinema' && frames.length > 0 ? (
        <div className="flex-1 relative">
          <CinemaMode
            frames={frames}
            projectId={projectId}
            onFrameClick={(frame) => handleManualFrameSelect(frame)}
            onUpdate={onUpdate}
            conceptLayerEnabled={conceptLayerEnabled}
          />
        </div>
      ) : (
      <ScrollArea className="flex-1">
        <div className="p-6">
          {frames.length === 0 && !autoGenActive ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <IconStoryboard size={40} />
              </div>
              <h3 className="empty-state-title">No Storyboard Yet</h3>
              <p className="empty-state-description">
                Generate a storyboard from your script using the sparkle button in the navigation rail.
              </p>
            </div>
          ) : frames.length === 0 && autoGenActive ? (
            null /* Progress is shown above, no empty state needed */
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="space-y-10">
              {Object.entries(framesByScene).map(([sceneNum, sceneFrames]) => (
                <div key={sceneNum} data-testid={`scene-group-${sceneNum}`}>
                  {/* Scene Header */}
                  <div className="flex items-center gap-4 mb-5">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[var(--border-default)] to-transparent" />
                    <span className="chip chip-gold">Scene {sceneNum}</span>
                    <span className="text-xs text-[var(--text-muted)]">{sceneFrames.length} shots</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[var(--border-default)] to-transparent" />
                  </div>
                  
                  {/* Frames Grid — group by shot_group_id */}
                  {(() => {
                    // Group consecutive frames by shot_group_id
                    const groups = [];
                    let currentGroup = null;

                    for (const frame of sceneFrames) {
                      if (frame.shot_group_id && currentGroup?.groupId === frame.shot_group_id) {
                        currentGroup.frames.push(frame);
                      } else if (frame.shot_group_id) {
                        currentGroup = { groupId: frame.shot_group_id, frames: [frame], movement: frame.camera_movement };
                        groups.push(currentGroup);
                      } else {
                        currentGroup = null;
                        groups.push({ groupId: null, frames: [frame], movement: null });
                      }
                    }

                    return (
                      <motion.div
                        className="space-y-4"
                        initial="hidden"
                        animate="visible"
                        variants={{ visible: { transition: { staggerChildren: 0.03 } } }}
                      >
                        {groups.map((group, gIdx) => (
                          <motion.div
                            key={group.groupId || group.frames[0].id}
                            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                          >
                            {group.groupId && group.frames.length > 1 ? (
                              <FrameFilmstrip
                                frames={group.frames}
                                movementType={group.movement}
                                onFrameClick={(f) => handleManualFrameSelect(f)}
                              />
                            ) : (
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {group.frames.map((frame) => (
                                  <FrameCard
                                    key={frame.id}
                                    frame={frame}
                                    isSelected={selectedFrame?.id === frame.id}
                                    onClick={() => handleManualFrameSelect(frame)}
                                    onEdit={() => handleManualFrameSelect(frame)}
                                    onDelete={(f) => setDeletingFrame(f)}
                                  />
                                ))}
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </motion.div>
                    );
                  })()}
                </div>
              ))}
            </div>
          ) : (
            /* Timeline View */
            <div className="space-y-4">
              {frames.map((frame, idx) => (
                <motion.div 
                  key={frame.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.02 }}
                  className="flex gap-4 items-start"
                >
                  <div className="w-40 aspect-video rounded-xl overflow-hidden bg-[var(--bg-muted)] flex-shrink-0">
                    {frame.status === "completed" ? (
                      <img src={getFrameImageUrl(frame.id)} alt="" className="w-full h-full object-cover" loading="lazy" crossOrigin="use-credentials" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {frame.status === 'generating' ? (
                          <Loader2 size={20} className="animate-spin text-[var(--color-gold-400)]" />
                        ) : (
                          <Film size={20} className="text-[var(--text-disabled)]" />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 py-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="chip chip-gold text-[9px]">S{frame.scene_number}</span>
                      <span className="chip text-[9px]">Shot {frame.frame_number}</span>
                      {frame.shot_type && <span className="text-xs text-[var(--text-secondary)] capitalize">{frame.shot_type.replace('-', ' ')}</span>}
                    </div>
                    <p className="text-xs text-[var(--text-muted)] line-clamp-2">{frame.prompt}</p>
                  </div>
                  <Button
                    onClick={() => setSelectedFrame(frame)}
                    className="btn-ghost btn-icon-sm"
                  >
                    <Edit3 size={16} />
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
      )}

      {/* Concept Board (concept layer enabled) */}
      <AnimatePresence>
        {conceptBoardAnchor && (
          <ConceptBoard
            projectId={projectId}
            anchor={conceptBoardAnchor}
            onClose={() => setConceptBoardAnchor(null)}
            onUpdate={onUpdate}
          />
        )}
      </AnimatePresence>

      {/* Lightbox (legacy / non-concept frames) */}
      <AnimatePresence>
        {selectedFrame && (
          <FrameLightbox
            frame={selectedFrame}
            frames={frames}
            onClose={() => setSelectedFrame(null)}
            onPrev={() => {
              if (currentFrameIndex > 0) setSelectedFrame(frames[currentFrameIndex - 1]);
            }}
            onNext={() => {
              if (currentFrameIndex < frames.length - 1) setSelectedFrame(frames[currentFrameIndex + 1]);
            }}
            onNavigate={(f) => setSelectedFrame(f)}
            onRegenerate={handleRegenerate}
            onVersions={() => { setVersionFrame(selectedFrame); setSelectedFrame(null); }}
            onUpdate={onUpdate}
          />
        )}
      </AnimatePresence>

      {/* Shot Version Panel (Slide-in) */}
      <AnimatePresence>
        {versionFrame && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-80 z-50 bg-[var(--bg-surface)] border-l border-[var(--border-subtle)] shadow-2xl"
          >
            <ShotVersionPanel
              projectId={projectId}
              frame={versionFrame}
              onRestored={() => { onUpdate(); setVersionFrame(null); }}
              onClose={() => setVersionFrame(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Shot Dialog */}
      {showAddDialog && (
        <AddShotDialog
          insertAfter={insertAfter}
          onSubmit={handleAddShot}
          onClose={() => { setShowAddDialog(false); setInsertAfter(null); }}
        />
      )}

      {/* Clear Storyboard Confirmation Dialog */}
      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent className="max-w-sm bg-[var(--bg-surface)] border-[var(--border-default)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2">
              <Trash2 size={20} className="text-[var(--color-error)]" />
              Clear Storyboard?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[var(--text-muted)]">
              This will delete all {frames.length} shots from your storyboard. This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowClearConfirm(false)} className="btn-ghost">
              Cancel
            </Button>
            <Button 
              onClick={handleClearStoryboard} 
              className="bg-[var(--color-error)] hover:bg-[var(--color-error)]/80 text-white"
            >
              <Trash2 size={14} />
              <span className="ml-1">Clear All</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Single Shot Confirmation Dialog */}
      <Dialog open={!!deletingFrame} onOpenChange={() => setDeletingFrame(null)}>
        <DialogContent className="max-w-sm bg-[var(--bg-surface)] border-[var(--border-default)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2">
              <Trash2 size={20} className="text-[var(--color-error)]" />
              Delete Shot?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {deletingFrame && (
              <div className="flex gap-3">
                {deletingFrame.status === "completed" && (
                  <div className="w-24 aspect-video rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={getFrameImageUrl(deletingFrame.id)}
                      alt="Shot to delete"
                      className="w-full h-full object-cover"
                      crossOrigin="use-credentials"
                    />
                  </div>
                )}
                <div>
                  <p className="text-sm text-[var(--text-muted)]">
                    Delete <span className="text-[var(--text-primary)] font-medium">Shot {deletingFrame.frame_number}</span> from Scene {deletingFrame.scene_number}?
                  </p>
                  <p className="text-xs text-[var(--text-disabled)] mt-1">This cannot be undone.</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setDeletingFrame(null)} className="btn-ghost">
              Cancel
            </Button>
            <Button 
              onClick={() => handleDeleteFrame(deletingFrame)} 
              className="bg-[var(--color-error)] hover:bg-[var(--color-error)]/80 text-white"
            >
              <Trash2 size={14} />
              <span className="ml-1">Delete</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
